import { Button, Group, Stack, Table, Text, TextInput } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { useState } from 'react';

import { ColumnFilter } from './ColumnFilter';
import { HeaderCell } from './HeaderCell';
import {
  activeCount,
  applyFilters,
  type Column,
  displayOf,
  filterableKeys,
  type Filters,
  matchesSearch,
  nextSort,
  type Row,
  type Sort,
  sortRows,
  toggleChoice,
  valuesOf,
} from './tableData';

export type { Column, Row } from './tableData';

/**
 * Every table in the arena. Callers say what the columns mean; how a table behaves — searching,
 * sorting, filtering — is decided here, once, because the second table written by hand is the one
 * that quietly lacks them.
 *
 * Which columns can be filtered is read from the data rather than declared, so a task's breakdown
 * gets filters on whatever its categorical columns turn out to be without anyone wiring them up.
 */
export function DataTable({
  columns,
  rows,
  minWidth = 560,
  emphasiseFirstColumn = false,
}: {
  columns: Column[];
  rows: Row[];
  minWidth?: number;
  emphasiseFirstColumn?: boolean;
}) {
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<Filters>({});
  const [sort, setSort] = useState<Sort | null>(null);

  const keys = columns.map(column => column.key);
  const filterable = filterableKeys(keys, rows);
  const shownAs = columns.map(column => column.text ?? ((row: Row) => displayOf(row[column.key])));
  const matching = applyFilters(rows, filters).filter(row => matchesSearch(row, shownAs, search));
  const shown = sortRows(matching, sort);
  const narrowed = shown.length !== rows.length;

  const clearNarrowing = () => {
    setSearch('');
    setFilters({});
  };

  return (
    <Stack gap={6}>
      <Group justify="space-between" gap="xs">
        <TextInput
          size="xs"
          w={240}
          placeholder="Search the table"
          leftSection={<IconSearch size={13} />}
          value={search}
          onChange={event => setSearch(event.currentTarget.value)}
        />
        {narrowed && (
          <Group gap={4}>
            <Text size="xs" c="dimmed">
              Showing {shown.length} of {rows.length}
            </Text>
            <Button size="compact-xs" variant="subtle" onClick={clearNarrowing}>
              Clear
            </Button>
          </Group>
        )}
      </Group>

      <Table.ScrollContainer minWidth={minWidth}>
        <Table striped highlightOnHover verticalSpacing="xs" horizontalSpacing="sm" fz="xs">
          <Table.Thead>
            <Table.Tr>
              {columns.map(column => (
                <HeaderCell
                  key={column.key}
                  column={column}
                  rows={rows}
                  sort={sort}
                  onSort={() => setSort(current => nextSort(current, column.key))}
                  filter={
                    filterable.includes(column.key) ? (
                      <ColumnFilter
                        label={column.label}
                        choices={valuesOf(rows, column.key)}
                        chosen={filters[column.key] ?? []}
                        onToggle={value =>
                          setFilters(current => toggleChoice(current, column.key, value))
                        }
                      />
                    ) : null
                  }
                />
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {shown.map((row, index) => (
              <Table.Tr key={index}>
                {columns.map((column, columnIndex) => (
                  <Table.Td key={column.key} ta={column.numeric ? 'right' : 'left'}>
                    {column.render ? (
                      column.render(row)
                    ) : (
                      <Text
                        size="xs"
                        ff={column.numeric ? 'monospace' : undefined}
                        fw={emphasiseFirstColumn && columnIndex === 0 ? 600 : 400}
                      >
                        {displayOf(row[column.key])}
                      </Text>
                    )}
                  </Table.Td>
                ))}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      {shown.length === 0 && (
        <Text size="xs" c="dimmed" ta="center" py="xs">
          Nothing matches. {activeCount(filters) > 0 && 'Try clearing the column filters.'}
        </Text>
      )}
    </Stack>
  );
}
